import {
  Button,
  Circle,
  HStack,
  IconButton,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ChatEllipsisIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import { useUsers } from "modules/providers/users-provider";
import React, { memo, useCallback, useMemo } from "react";
import colors from "app/colors";
import { CHAT_DRAFT_ID, TextAlignment } from "modules/utils/Constants";
import useMessageThreadDraft from "modules/hooks/use-message-thread-draft";
import { DRAWER_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import { MessageThreadType, UserRoleType } from "gql/graphql";

interface IProps {
  userId: string;
  displayText?: boolean;
  textAlignment?: TextAlignment;
}

const NewChatButton = ({
  userId,
  displayText = true,
  textAlignment = "inline",
}: IProps) => {
  const { openDrawer } = useOverlay();
  const { createDraft } = useMessageThreadDraft({
    draftId: CHAT_DRAFT_ID,
  });
  const { usersById } = useUsers();

  const user = useMemo(() => usersById[userId], [usersById, userId]);

  const onNewChatClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      await createDraft({
        type: MessageThreadType.Chat,
        draftType: "new-thread",
        userIds: [userId],
      });
      openDrawer({
        type: DRAWER_TYPE.COMPOSE_CHAT,
      });
    },
    [createDraft, openDrawer, userId],
  );

  if (!user || !user.active || user.roleType === UserRoleType.Email) {
    return null;
  }

  if (!displayText) {
    return (
      <IconButton
        onClick={onNewChatClicked}
        aria-label="New Chat"
        variant="hover"
        bg={colors.blue}
        rounded="full"
        size="sm"
        icon={<ChatEllipsisIcon boxSize={6} fill={colors.white} />}
      />
    );
  }

  if (textAlignment === "inline") {
    return (
      <Button
        onClick={onNewChatClicked}
        aria-label="New Chat"
        variant="ghost"
        height="auto"
        px={0}
      >
        <HStack spacing={1}>
          <Circle bg={colors.blue} size={6}>
            <ChatEllipsisIcon boxSize={4} fill={colors.white} />
          </Circle>
          {displayText && (
            <Text
              fontSize={10}
              fontWeight={500}
              color={colors.gray50}
              textAlign="center"
            >
              New Chat
            </Text>
          )}
        </HStack>
      </Button>
    );
  }

  return (
    <Button
      onClick={onNewChatClicked}
      aria-label="New Chat"
      variant="hover"
      height="auto"
      px={0}
    >
      <VStack spacing={1}>
        <Circle bg={colors.blue} size={6}>
          <ChatEllipsisIcon boxSize={4} fill={colors.white} />
        </Circle>
        <Text
          fontSize={10}
          fontWeight={500}
          color={colors.gray50}
          textAlign="center"
        >
          New
          <br />
          Chat
        </Text>
      </VStack>
    </Button>
  );
};

export default memo(NewChatButton);
